import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { getFreelancerRatingsApi } from "../freelancerRatingsApi";

import searchIcon from "../../../img/search.svg";
import "./FreelancerRatingsPage.css";

type FreelancerRatingsRow = {
  freelancerId: string;
  storeNames: string[];
  fullName: string;
  contato: string | null;
  cpf: string;
  stars: number | null;
};

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

const formatCPF = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "").padStart(11, "0").slice(-11);
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

const Stars: React.FC<{ value: number | null | undefined }> = ({ value }) => {
  const v = typeof value === "number" ? clamp(value, 0, 5) : null;
  const pct = v == null ? 0 : (v / 5) * 100;

  return (
    <div className="Stars" title={v == null ? "-" : `${v.toFixed(2)} / 5`}>
      <div className="StarsBar">
        <div className="StarsBack">★★★★★</div>
        <div className="StarsFront" style={{ width: `${pct}%` }}>
          ★★★★★
        </div>
      </div>
    </div>
  );
};

const FreelancerRatingsPage: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((s: any) => (s as any).auth?.accessToken);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<FreelancerRatingsRow[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalItens, setTotalItens] = useState(0);

  // input digitado vs busca aplicada
  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItens / limit)),
    [totalItens, limit]
  );

  const load = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await getFreelancerRatingsApi(token, { page, limit, nome: qApplied });
      setRows(data.items ?? []);
      setTotalItens(data.totalItens ?? 0);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao carregar tabela de freelancers.");
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, qApplied]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = useCallback(() => {
    const next = qInput.trim();
    setQApplied(next);
    setPage(1);
    if (page === 1 && next === qApplied) load();
  }, [qInput, page, qApplied, load]);

  const filtered = rows;

  return (
    <div className="StoreMenuPage">
      <div className="LoginLogoWrap">{/* seu logo aqui, se quiser */}</div>

      <div className="freelancer-page freelancer-page--orange">
        <div className="freelancer-container">
          <h1 className="StoreMenuTitle">Freelancers</h1>
          <p className="freelancer-pageSubtitle">Informações gerais</p>

          <div className="freelancer-card-table">
            {/* topo do card: subtítulo + busca */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="PagerButton PagerButtonLight"
                onClick={() => navigate("/")}
                disabled={loading}
              >
                Home
              </button>

              <p className="freelancer-subtitle" style={{ margin: 0 }}>
                Lista de freelancers cadastrados
              </p>

              <div className="FreelancerSearch FreelancerSearch--mobileWrap">
                <input
                  className="freelancer-input FreelancerSearchInput"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Buscar por loja, nome, contato ou CPF"
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applySearch();
                    }
                  }}
                />

                <button
                  type="button"
                  className="PagerButton FreelancerSearchBtn"
                  onClick={applySearch}
                  disabled={loading}
                  title="Buscar"
                >
                  <img className="FreelancerSearchIcon" src={searchIcon} alt="Buscar" />
                </button>
              </div>
            </div>

            <div style={{ marginTop: 16 }} />

            {loading ? (
              <div className="BoxEmpty">Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className="BoxEmpty">Nenhum freelancer encontrado.</div>
            ) : (
              <>
                <div className="RatingsTableWrap">
                  <table className="RatingsTable">
                    <thead>
                      <tr>
                        <th className="amplesoft">Nome completo</th>
                        <th className="amplesoft">Contato</th>
                        <th className="amplesoft">CPF</th>
                        <th className="amplesoft RatingsStarsCol">Estrelas</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r.freelancerId}>
                          <td className="RatingsCell">{r.fullName}</td>
                          <td className="RatingsCell">{r.contato ?? "-"}</td>
                          <td className="RatingsCell">{formatCPF(r.cpf)}</td>
                          <td className="RatingsCell RatingsStarsCol">
                            <Stars value={r.stars} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="RatingsCards">
                  <div className="DraftList">
                    {filtered.map((r) => (
                      <div key={r.freelancerId} className="DraftItem RatingsCard">
                        <div className="DraftInfo">
                          <div className="DraftTitle">{r.fullName}</div>
                          <div className="DraftSub">
                            {r.storeNames?.length ? r.storeNames.join(", ") : "-"}
                          </div>
                        </div>

                        <div style={{ marginTop: 10 }} />

                        <div className="SelectedInfoBox">
                          <div className="SelectedInfoRow">
                            <span className="SelectedInfoLabel">Contato</span>
                            <span className="SelectedInfoValue">{r.contato ?? "-"}</span>
                          </div>
                        </div>

                        <div className="SelectedInfoBox">
                          <div className="SelectedInfoRow">
                            <span className="SelectedInfoLabel">CPF</span>
                            <span className="SelectedInfoValue">{formatCPF(r.cpf)}</span>
                          </div>
                        </div>

                        <div className="SelectedInfoBox">
                          <div className="SelectedInfoRow">
                            <span className="SelectedInfoLabel">Estrelas</span>
                            <span className="SelectedInfoValue">
                              <Stars value={r.stars} />
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="Pager RatingsPager">
                  <button
                    type="button"
                    className="PagerButton PagerButtonLight"
                    disabled={loading || page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Ant.
                  </button>

                  <div className="PagerInfo">
                    {page} de {totalPages}
                  </div>

                  <button
                    type="button"
                    className="PagerButton"
                    disabled={loading || page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Próx.
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerRatingsPage;
